 
 
 
/* eslint-disable no-prototype-builtins */
 
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { PersistenceService, PerformanceMetrics, SyncResult } from './PersistenceService';

// Health monitoring types
export interface SyncHealthMetrics {
  totalOperations: number;
  successfulOperations: number;
  failedOperations: number;
  successRate: number;
  averageLatency: number;
  peakLatency: number;
  throughput: number;
  queueDepth: number;
  storageUtilization: {
    localStorage: number;
    indexedDB: number;
  };
  lastUpdateTime: number;
}

export interface SyncAlert {
  id: string;
  type: 'error' | 'warning' | 'info';
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  details: Record<string, unknown>;
  timestamp: number;
  resolved: boolean;
}

export interface AlertThresholds {
  errorRateThreshold: number;
  latencyThreshold: number;
  queueDepthThreshold: number;
  storageThreshold: number;
  throughputThreshold: number;
}

export interface OperationHealth {
  operationType: string;
  health: 'healthy' | 'degraded' | 'unhealthy';
  metrics: {
    successRate: number;
    avgLatency: number;
    errorCount: number;
    lastError?: string;
  };
}

// Default alert thresholds
const DEFAULT_THRESHOLDS: AlertThresholds = {
  errorRateThreshold: 10, // 10% error rate
  latencyThreshold: 1000, // 1 second
  queueDepthThreshold: 100, // 100 pending operations
  storageThreshold: 90, // 90% storage utilization
  throughputThreshold: 10, // 10 operations per second minimum
};

// Sync status type from syncMiddleware
type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error' | 'conflict';

interface StoreSyncStatus {
  storeName: string;
  status: SyncStatus;
  pendingCount: number;
  lastUpdated: number;
}

export class SyncHealthService {
  private static instance: SyncHealthService;
  private persistenceService: PersistenceService;
  private alerts: Map<string, SyncAlert> = new Map();
  private healthMetrics: SyncHealthMetrics;
  private thresholds: AlertThresholds;
  private metricsHistory: SyncHealthMetrics[] = [];
  private monitoringInterval: ReturnType<typeof setInterval> | null = null;
  private listeners: Set<(event: string, data: unknown) => void> = new Set();
  private storeSyncStatus: Map<string, StoreSyncStatus> = new Map();

  private constructor(
    persistenceService: PersistenceService,
    thresholds: Partial<AlertThresholds> = {}
  ) {
    this.persistenceService = persistenceService;
    this.thresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
    
    this.healthMetrics = {
      totalOperations: 0,
      successfulOperations: 0,
      failedOperations: 0,
      successRate: 100,
      averageLatency: 0,
      peakLatency: 0,
      throughput: 0,
      queueDepth: 0,
      storageUtilization: {
        localStorage: 0,
        indexedDB: 0,
      },
      lastUpdateTime: Date.now(),
    };

    // Subscribe to persistence service events
    this.subscribeToEvents();
  }

  static getInstance(
    persistenceService: PersistenceService,
    thresholds?: Partial<AlertThresholds>
  ): SyncHealthService {
    if (!SyncHealthService.instance) {
      SyncHealthService.instance = new SyncHealthService(persistenceService, thresholds);
    }
    return SyncHealthService.instance;
  }

  // Start continuous health monitoring
  startMonitoring(intervalMs: number = 5000): void {
    if (this.monitoringInterval) {
      return;
    }

    this.monitoringInterval = setInterval(() => {
      this.updateHealthMetrics();
      this.checkHealthThresholds();
      this.pruneOldAlerts();
    }, intervalMs);

    // Initial update
    this.updateHealthMetrics();
  }

  // Stop health monitoring
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  // Subscribe to persistence service events
  private subscribeToEvents(): void {
    this.persistenceService.addEventListener((event, data) => {
      switch (event) {
        case 'operationComplete':
          this.handleOperationComplete(data as any);
          break;
        case 'operationFailed':
          this.handleOperationFailed(data as any);
          break;
        case 'metric':
          this.handleNewMetric(data as PerformanceMetrics);
          break;
      }
    });
  }

  // Handle successful operation
  private handleOperationComplete(operation: any): void {
    this.healthMetrics.successfulOperations++;
    this.healthMetrics.totalOperations++;
    this.updateSuccessRate();
    this.emit('operationComplete', operation);
  }

  // Handle failed operation
  private handleOperationFailed(data: { operation: any; error: Error }): void {
    this.healthMetrics.failedOperations++;
    this.healthMetrics.totalOperations++;
    this.updateSuccessRate();
    
    // Create alert for operation failure
    const alert: SyncAlert = {
      id: `alert-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      type: 'error',
      severity: 'high',
      message: `Sync operation failed: ${data.error.message}`,
      details: {
        operation: data.operation,
        error: data.error.message,
        stack: data.error.stack,
      },
      timestamp: Date.now(),
      resolved: false,
    };

    this.addAlert(alert);
    this.emit('operationFailed', data);
  }

  // Handle new performance metric
  private handleNewMetric(metric: PerformanceMetrics): void {
    if (metric.duration > this.healthMetrics.peakLatency) {
      this.healthMetrics.peakLatency = metric.duration;
    }
  }

  // Update health metrics
  private updateHealthMetrics(): void {
    const now = Date.now();
    const timeDelta = (now - this.healthMetrics.lastUpdateTime) / 1000; // seconds

    // Get recent metrics from persistence service
    const recentMetrics = this.persistenceService.getPerformanceMetrics(
      undefined,
      { start: now - 60000, end: now } // Last minute
    );

    // Calculate average latency
    if (recentMetrics.length > 0) {
      const totalLatency = recentMetrics.reduce((sum, m) => sum + m.duration, 0);
      this.healthMetrics.averageLatency = totalLatency / recentMetrics.length;
    }

    // Calculate throughput (operations per second)
    if (timeDelta > 0) {
      const operationsSinceLastUpdate = recentMetrics.length;
      this.healthMetrics.throughput = operationsSinceLastUpdate / timeDelta;
    }

    // Update queue depth (this would need to be exposed by PersistenceService)
    // For now, we'll estimate based on recent operations
    this.healthMetrics.queueDepth = 0; // Placeholder

    // Update storage utilization
    this.updateStorageUtilization();

    // Record metrics history
    this.metricsHistory.push({ ...this.healthMetrics });
    if (this.metricsHistory.length > 1000) {
      this.metricsHistory = this.metricsHistory.slice(-1000);
    }

    this.healthMetrics.lastUpdateTime = now;
    this.emit('metricsUpdated', this.healthMetrics);
  }

  // Update storage utilization metrics
  private async updateStorageUtilization(): Promise<void> {
    try {
      // Check localStorage usage
      const localStorageUsed = this.getLocalStorageSize();
      const localStorageLimit = 10 * 1024 * 1024; // 10MB typical limit
      this.healthMetrics.storageUtilization.localStorage = 
        (localStorageUsed / localStorageLimit) * 100;

      // Check IndexedDB usage (this is an estimate)
      const indexedDBEstimate = await this.estimateIndexedDBUsage();
      const indexedDBLimit = 1024 * 1024 * 1024; // 1GB typical limit
      this.healthMetrics.storageUtilization.indexedDB = 
        (indexedDBEstimate / indexedDBLimit) * 100;
    } catch (error) {
      console.error('Failed to update storage utilization:', error);
    }
  }

  // Check health thresholds and create alerts
  private checkHealthThresholds(): void {
    // Check error rate
    if (this.healthMetrics.successRate < (100 - this.thresholds.errorRateThreshold)) {
      this.createThresholdAlert(
        'error',
        'high',
        `Error rate exceeded threshold: ${(100 - this.healthMetrics.successRate).toFixed(2)}%`,
        { successRate: this.healthMetrics.successRate }
      );
    }

    // Check latency
    if (this.healthMetrics.averageLatency > this.thresholds.latencyThreshold) {
      this.createThresholdAlert(
        'warning',
        'medium',
        `Average latency exceeded threshold: ${this.healthMetrics.averageLatency.toFixed(2)}ms`,
        { averageLatency: this.healthMetrics.averageLatency }
      );
    }

    // Check queue depth
    if (this.healthMetrics.queueDepth > this.thresholds.queueDepthThreshold) {
      this.createThresholdAlert(
        'warning',
        'high',
        `Queue depth exceeded threshold: ${this.healthMetrics.queueDepth} operations`,
        { queueDepth: this.healthMetrics.queueDepth }
      );
    }

    // Check storage utilization
    if (this.healthMetrics.storageUtilization.localStorage > this.thresholds.storageThreshold) {
      this.createThresholdAlert(
        'warning',
        'high',
        `LocalStorage utilization exceeded threshold: ${this.healthMetrics.storageUtilization.localStorage.toFixed(2)}%`,
        { storageUtilization: this.healthMetrics.storageUtilization.localStorage }
      );
    }

    if (this.healthMetrics.storageUtilization.indexedDB > this.thresholds.storageThreshold) {
      this.createThresholdAlert(
        'warning',
        'high',
        `IndexedDB utilization exceeded threshold: ${this.healthMetrics.storageUtilization.indexedDB.toFixed(2)}%`,
        { storageUtilization: this.healthMetrics.storageUtilization.indexedDB }
      );
    }

    // Check throughput
    if (this.healthMetrics.throughput < this.thresholds.throughputThreshold) {
      this.createThresholdAlert(
        'info',
        'low',
        `Throughput below threshold: ${this.healthMetrics.throughput.toFixed(2)} ops/sec`,
        { throughput: this.healthMetrics.throughput }
      );
    }
  }

  // Create threshold-based alert
  private createThresholdAlert(
    type: SyncAlert['type'],
    severity: SyncAlert['severity'],
    message: string,
    details: Record<string, unknown>
  ): void {
    const alertKey = `threshold-${type}-${message.substring(0, 20)}`;
    
    // Don't create duplicate alerts
    if (this.alerts.has(alertKey)) {
      const existingAlert = this.alerts.get(alertKey)!;
      if (!existingAlert.resolved && Date.now() - existingAlert.timestamp < 60000) {
        return;
      }
    }

    const alert: SyncAlert = {
      id: alertKey,
      type,
      severity,
      message,
      details,
      timestamp: Date.now(),
      resolved: false,
    };

    this.addAlert(alert);
  }

  // Get health report for specific operation types
  getOperationHealth(operationType?: string): OperationHealth[] {
    const operationTypes = new Set<string>();
    const metrics = this.persistenceService.getPerformanceMetrics();

    // Collect all operation types
    metrics.forEach(m => operationTypes.add(m.operationType));

    const healthReports: OperationHealth[] = [];

    for (const opType of operationTypes) {
      if (operationType && opType !== operationType) {
        continue;
      }

      const opMetrics = metrics.filter(m => m.operationType === opType);
      const successCount = opMetrics.filter(m => m.success).length;
      const errorCount = opMetrics.filter(m => !m.success).length;
      const totalLatency = opMetrics.reduce((sum, m) => sum + m.duration, 0);
      const lastError = opMetrics
        .filter(m => !m.success && m.error)
        .sort((a, b) => b.timestamp - a.timestamp)[0]?.error;

      const successRate = opMetrics.length > 0 
        ? (successCount / opMetrics.length) * 100 
        : 100;
      
      const avgLatency = opMetrics.length > 0 
        ? totalLatency / opMetrics.length 
        : 0;

      let health: OperationHealth['health'] = 'healthy';
      if (successRate < 90 || avgLatency > this.thresholds.latencyThreshold) {
        health = 'degraded';
      }
      if (successRate < 70 || avgLatency > this.thresholds.latencyThreshold * 2) {
        health = 'unhealthy';
      }

      healthReports.push({
        operationType: opType,
        health,
        metrics: {
          successRate,
          avgLatency,
          errorCount,
          lastError,
        },
      });
    }

    return healthReports;
  }

  // Get current health metrics
  getHealthMetrics(): SyncHealthMetrics {
    return { ...this.healthMetrics };
  }

  // Get metrics history
  getMetricsHistory(limit?: number): SyncHealthMetrics[] {
    if (limit) {
      return this.metricsHistory.slice(-limit);
    }
    return [...this.metricsHistory];
  }

  // Get active alerts
  getActiveAlerts(severity?: SyncAlert['severity']): SyncAlert[] {
    const alerts = Array.from(this.alerts.values())
      .filter(alert => !alert.resolved);

    if (severity) {
      return alerts.filter(alert => alert.severity === severity);
    }

    return alerts;
  }

  // Get all alerts
  getAllAlerts(): SyncAlert[] {
    return Array.from(this.alerts.values());
  }

  // Resolve an alert
  resolveAlert(alertId: string): void {
    const alert = this.alerts.get(alertId);
    if (alert) {
      alert.resolved = true;
      this.emit('alertResolved', alert);
    }
  }

  // Add alert
  private addAlert(alert: SyncAlert): void {
    this.alerts.set(alert.id, alert);
    this.emit('alertCreated', alert);
  }

  // Prune old resolved alerts
  private pruneOldAlerts(): void {
    const cutoffTime = Date.now() - 24 * 60 * 60 * 1000; // 24 hours
    
    for (const [id, alert] of this.alerts) {
      if (alert.resolved && alert.timestamp < cutoffTime) {
        this.alerts.delete(id);
      }
    }
  }

  // Update thresholds
  updateThresholds(thresholds: Partial<AlertThresholds>): void {
    this.thresholds = { ...this.thresholds, ...thresholds };
    this.emit('thresholdsUpdated', this.thresholds);
  }

  // Get thresholds
  getThresholds(): AlertThresholds {
    return { ...this.thresholds };
  }

  // Generate health report
  generateHealthReport(): {
    summary: {
      status: 'healthy' | 'degraded' | 'critical';
      score: number;
      lastUpdated: number;
    };
    metrics: SyncHealthMetrics;
    operations: OperationHealth[];
    alerts: {
      critical: number;
      high: number;
      medium: number;
      low: number;
      total: number;
    };
    recommendations: string[];
  } {
    const activeAlerts = this.getActiveAlerts();
    const operationHealth = this.getOperationHealth();
    
    // Calculate health score
    let healthScore = 100;
    healthScore -= (100 - this.healthMetrics.successRate) * 2;
    healthScore -= Math.min(20, (this.healthMetrics.averageLatency / this.thresholds.latencyThreshold) * 10);
    healthScore -= activeAlerts.filter(a => a.severity === 'critical').length * 20;
    healthScore -= activeAlerts.filter(a => a.severity === 'high').length * 10;
    healthScore = Math.max(0, Math.min(100, healthScore));

    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';
    if (healthScore < 70) status = 'degraded';
    if (healthScore < 40) status = 'critical';

    // Generate recommendations
    const recommendations: string[] = [];
    
    if (this.healthMetrics.successRate < 90) {
      recommendations.push('Investigate high error rate in sync operations');
    }
    
    if (this.healthMetrics.averageLatency > this.thresholds.latencyThreshold) {
      recommendations.push('Consider optimizing sync operations to reduce latency');
    }
    
    if (this.healthMetrics.storageUtilization.localStorage > 80) {
      recommendations.push('LocalStorage usage is high, consider cleanup or migration to IndexedDB');
    }
    
    if (this.healthMetrics.queueDepth > 50) {
      recommendations.push('Sync queue is building up, check for bottlenecks');
    }

    return {
      summary: {
        status,
        score: healthScore,
        lastUpdated: this.healthMetrics.lastUpdateTime,
      },
      metrics: this.getHealthMetrics(),
      operations: operationHealth,
      alerts: {
        critical: activeAlerts.filter(a => a.severity === 'critical').length,
        high: activeAlerts.filter(a => a.severity === 'high').length,
        medium: activeAlerts.filter(a => a.severity === 'medium').length,
        low: activeAlerts.filter(a => a.severity === 'low').length,
        total: activeAlerts.length,
      },
      recommendations,
    };
  }

  // Update sync status for a specific store
  updateSyncStatus(storeName: string, status: SyncStatus, pendingCount: number = 0): void {
    const currentStatus = this.storeSyncStatus.get(storeName);
    const newStatus: StoreSyncStatus = {
      storeName,
      status,
      pendingCount,
      lastUpdated: Date.now()
    };
    
    this.storeSyncStatus.set(storeName, newStatus);
    
    // Update queue depth based on all stores
    let totalPending = 0;
    this.storeSyncStatus.forEach(storeStatus => {
      totalPending += storeStatus.pendingCount;
    });
    this.healthMetrics.queueDepth = totalPending;
    
    // Emit status change event
    this.emit('syncStatusChanged', {
      storeName,
      oldStatus: currentStatus?.status,
      newStatus: status,
      pendingCount
    });
    
    // Create alert if needed
    if (status === 'error' && (!currentStatus || currentStatus.status !== 'error')) {
      this.createThresholdAlert(
        'error',
        'high',
        `Sync error in store: ${storeName}`,
        { storeName, status }
      );
    }
  }
  
  // Update sync metrics
  updateSyncMetrics(metrics: Partial<SyncHealthMetrics>): void {
    // Update provided metrics
    Object.assign(this.healthMetrics, metrics);
    
    // Recalculate success rate if operations counts changed
    if (metrics.totalOperations !== undefined || 
        metrics.successfulOperations !== undefined ||
        metrics.failedOperations !== undefined) {
      this.updateSuccessRate();
    }
    
    // Update last update time
    this.healthMetrics.lastUpdateTime = Date.now();
    
    // Emit metrics update event
    this.emit('metricsUpdated', this.healthMetrics);
  }
  
  // Record a sync error
  recordSyncError(storeName: string, errorMessage: string): void {
    // Update failed operations count
    this.healthMetrics.failedOperations++;
    this.healthMetrics.totalOperations++;
    this.updateSuccessRate();
    
    // Create error alert
    const alert: SyncAlert = {
      id: `sync-error-${storeName}-${Date.now()}`,
      type: 'error',
      severity: 'high',
      message: `Sync error in ${storeName}: ${errorMessage}`,
      details: {
        storeName,
        error: errorMessage,
        timestamp: Date.now()
      },
      timestamp: Date.now(),
      resolved: false
    };
    
    this.addAlert(alert);
    
    // Update sync status to error
    this.updateSyncStatus(storeName, 'error');
    
    // Emit error event
    this.emit('syncError', {
      storeName,
      error: errorMessage,
      timestamp: Date.now()
    });
  }

  // Utility methods
  private updateSuccessRate(): void {
    if (this.healthMetrics.totalOperations > 0) {
      this.healthMetrics.successRate = 
        (this.healthMetrics.successfulOperations / this.healthMetrics.totalOperations) * 100;
    }
  }

  private getLocalStorageSize(): number {
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    return totalSize * 2; // UTF-16 encoding
  }

  private async estimateIndexedDBUsage(): Promise<number> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return estimate.usage || 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }

  // Event handling
  private emit(event: string, data: unknown): void {
    this.listeners.forEach(listener => listener(event, data));
  }

  addEventListener(listener: (event: string, data: unknown) => void): void {
    this.listeners.add(listener);
  }

  removeEventListener(listener: (event: string, data: unknown) => void): void {
    this.listeners.delete(listener);
  }

  // Cleanup
  destroy(): void {
    this.stopMonitoring();
    this.alerts.clear();
    this.metricsHistory = [];
    this.listeners.clear();
    SyncHealthService.instance = null as any;
  }
}

// Export singleton getter
export const getSyncHealthService = (
  persistenceService: PersistenceService,
  thresholds?: Partial<AlertThresholds>
): SyncHealthService => {
  return SyncHealthService.getInstance(persistenceService, thresholds);
};